import { ResourceType } from '@cotera/api';

/**
 * A favorite's identity as a single string, for `Set` membership and React keys.
 *
 * Favorites are keyed by (type, id), not id alone: ids are UUIDs and collisions
 * are not a real risk, but the pair is what the API speaks and what the sidebar
 * has to hand back when it reorders, so carrying both everywhere avoids a lookup
 * that could only ever guess at the type.
 */
export type FavoriteKey = string & { readonly __brand: 'FavoriteKey' };

export const favoriteKey = (type: ResourceType, id: string): FavoriteKey =>
  `${type}:${id}` as FavoriteKey;

/** A resource named the way the favorites API names it. */
export type FavoriteRef = {
  type: ResourceType;
  id: string;
};

/**
 * The list with `from` lifted out and dropped at `to`, where both index into the
 * list as it is currently drawn.
 *
 * Pure and exported so the reorder rule is testable without a DOM: the sidebar's
 * drop handler is otherwise the only place this arithmetic exists, and dropping
 * an item *below* its own position is the case that is easy to get wrong (the
 * list is one shorter once the item is lifted, so the target index shifts).
 */
export function moveFavorite<T>(items: readonly T[], from: number, to: number) {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= items.length ||
    to > items.length
  ) {
    return [...items];
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) {
    return [...items];
  }
  next.splice(from < to ? to - 1 : to, 0, moved);
  return next;
}
